import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate } from "@angular/router";
import { Store } from "@ngrx/store";
import {
	AssessmentfulfilmentMode,
	AssessmentStatus,
	CustomerAssessmentsAction,
	CustomerAssessmentsConfigService,
} from "@obg/ngx.common";
import {
	AuthenticationStatus,
	AuthSelector,
	CORE_ROUTE,
	CustomerComplianceService,
	CustomerManagementSelector,
	DisallowedActionCodes,
} from "@obg/ngx.core";
import { CoreContentState } from "@obg/ngx.core.content";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, iif, Observable, of } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";

import { ASSESSMENTS_REDIRECT_ROUTES } from "./customer-assessments.const";

@Injectable({
	providedIn: "root",
})
export class CustomerAssessmentsGuard implements CanActivate {
	constructor(
		private customerComplianceService: CustomerComplianceService,
		private assessmentsConfigService: CustomerAssessmentsConfigService,
		private store: Store<CoreContentState>
	) {}

	canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
		const { data } = route;
		if (!data || !data.actionType) {
			return of(true);
		}

		return this.store.select(AuthSelector.getAuthenticationStatus).pipe(
			filter((authStatus) => authStatus !== AuthenticationStatus.Pending),
			switchMap((authStatus) =>
				iif(
					() => authStatus === AuthenticationStatus.Authenticated,
					combineLatest([
						this.store.select(CustomerManagementSelector.getAllowedAction).pipe(ignoreNil()),
						this.customerComplianceService.kycStatusCheck$(data.actionType),
						this.store.select(RouteSelector.getCurrent),
						this.store.select(RouteSelector.getReferer),
					]).pipe(
						map(([allowedActions, kyc, currentRoute, referer]) => {
							const currentActionOptions = allowedActions[kyc.action];
							const disallowedReasons = currentActionOptions?.disallowedReasons;
							const isCustomerAssessmentsEnabled =
								this.assessmentsConfigService.getIsCustomerAssessmentsEnabled();
							const isBlocked = !!disallowedReasons?.includes(
								DisallowedActionCodes.M_CURRENTCUSTOMER_ASSESSMENTREQUIRED
							);

							if (!isBlocked || !isCustomerAssessmentsEnabled) {
								return true;
							}

							const isLoginRoute = currentRoute?.name === CORE_ROUTE.auth.login;
							const hasReturnUrl = !!currentRoute?.queryParams?.returnUrl;

							if (!hasReturnUrl && !isLoginRoute) {
								this.store.dispatch(
									CustomerAssessmentsAction.getCustomerAssessments({
										fulfillmentMode: AssessmentfulfilmentMode.Required,
										status: AssessmentStatus.Requested,
									})
								);
							}

							if (hasReturnUrl) {
								this.store.dispatch(RouteAction.updateQueryParams({ returnUrl: undefined }));
							}

							if (!currentRoute || isLoginRoute) {
								const fallbackRoute = referer?.fallbackRoute;
								fallbackRoute && ASSESSMENTS_REDIRECT_ROUTES.includes(fallbackRoute)
									? this.store.dispatch(RouteAction.navigate(fallbackRoute))
									: this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));
							}

							return false;
						})
					),
					of(true)
				)
			)
		);
	}
}
